import React, { useState, useEffect } from 'react';
import { Shield, AlertTriangle, Info, Clock, Users, FileText, Search, Target, Activity, ChevronDown, ChevronRight, BookOpen, Zap, Eye, Brain, Server, Network, Database, Lock, ArrowRight, CheckCircle, AlertCircle, XCircle } from 'lucide-react';

const AlertStrikeSIEM = () => {
  const [activeSection, setActiveSection] = useState<string>('overview');
  const [expandedSeverity, setExpandedSeverity] = useState<string | null>(null);
  const [expandedSOP, setExpandedSOP] = useState<string | null>(null);
  const [systemStatus, setSystemStatus] = useState({
    alertsProcessed: Math.floor(Math.random() * 1000) + 2500,
    activeIncidents: Math.floor(Math.random() * 10) + 5,
    averageResponseTime: Math.floor(Math.random() * 5) + 8,
    lastUpdate: new Date().toLocaleTimeString()
  });

  // Update system stats periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setSystemStatus(prev => ({
        alertsProcessed: prev.alertsProcessed + Math.floor(Math.random() * 3),
        activeIncidents: Math.max(0, prev.activeIncidents + (Math.random() > 0.7 ? 1 : -1)),
        averageResponseTime: Math.max(5, Math.min(15, prev.averageResponseTime + (Math.random() > 0.5 ? 0.5 : -0.5))),
        lastUpdate: new Date().toLocaleTimeString()
      }));
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  const severityLevels = {
    critical: {
      level: 'Critical',
      range: '10-15',
      color: 'text-red-500',
      bgColor: 'bg-red-900/20',
      borderColor: 'border-red-600/50',
      icon: <AlertTriangle className="h-5 w-5" />,
      description: 'Immediate threat requiring instant response',
      responseTime: '< 15 minutes',
      escalation: 'Immediate L2/L3 escalation + Management notification'
    },
    high: {
      level: 'High',
      range: '7-9',
      color: 'text-orange-500',
      bgColor: 'bg-orange-900/20',
      borderColor: 'border-orange-600/50',
      icon: <AlertCircle className="h-5 w-5" />,
      description: 'Significant security event requiring urgent attention',
      responseTime: '< 30 minutes',
      escalation: 'L2 escalation within 1 hour if not resolved'
    },
    medium: {
      level: 'Medium',
      range: '4-6',
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-900/20',
      borderColor: 'border-yellow-600/50',
      icon: <Info className="h-5 w-5" />,
      description: 'Potential security issue requiring investigation',
      responseTime: '< 1 hours',
      escalation: 'Standard investigation procedures'
    },
    low: {
      level: 'Low',
      range: '1-3',
      color: 'text-blue-500',
      bgColor: 'bg-blue-900/20',
      borderColor: 'border-blue-600/50',
      icon: <CheckCircle className="h-5 w-5" />,
      description: 'Informational event or minor security concern',
      responseTime: '< 4 hours',
      escalation: 'Document and monitor for patterns'
    }
  };

  const sopProcedures = [
    {
      id: 'sop1',
      title: 'Alert Triage and Initial Analysis (L1)',
      level: 'L1 Analyst',
      description: 'Initial alert handling and triage procedures',
      icon: <Target className="h-5 w-5 text-blue-500" />
    },
    {
      id: 'sop2',
      title: 'Incident Investigation and Analysis (L2)',
      level: 'L2 Analyst',
      description: 'Deep dive investigation and forensic analysis',
      icon: <Search className="h-5 w-5 text-orange-500" />
    },
    {
      id: 'sop3',
      title: 'Containment, Eradication, and Recovery (L2/L3)',
      level: 'L2/L3 Analyst',
      description: 'Incident response and system recovery',
      icon: <Shield className="h-5 w-5 text-red-500" />
    },
    {
      id: 'sop4',
      title: 'Proactive Threat Hunting (L3)',
      level: 'L3 Analyst',
      description: 'Advanced threat hunting and detection improvement',
      icon: <Eye className="h-5 w-5 text-purple-500" />
    }
  ];

  const navigationSections = [
    { id: 'overview', label: 'Overview', icon: <BookOpen className="h-4 w-4" /> },
    { id: 'roles', label: 'Roles & Responsibilities', icon: <Users className="h-4 w-4" /> },
    { id: 'severities', label: 'Alert Severities', icon: <AlertTriangle className="h-4 w-4" /> },
    { id: 'procedures', label: 'Standard Procedures', icon: <FileText className="h-4 w-4" /> },
    { id: 'workflows', label: 'Detailed Workflows', icon: <Activity className="h-4 w-4" /> },
    { id: 'integration', label: 'ShieldX Intel AI', icon: <Brain className="h-4 w-4" /> }
  ];

  return (
    <main className="container mx-auto px-3 sm:px-6 py-4 sm:py-8 relative">
      {/* HUD-Style Top Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-10 p-1 border border-red-900/30 rounded-md bg-black/30 backdrop-blur-sm">
        <div className="flex flex-wrap items-center gap-2 px-2 py-2 sm:py-0 sm:gap-4 sm:px-4">
          <div className="text-red-500 font-mono text-xs sm:text-sm">
            <span className="text-slate-400">SIEM:</span> ACTIVE
          </div>
          <div className="text-red-500 font-mono text-xs sm:text-sm">
            <span className="text-slate-400">XDR:</span> ENABLED
          </div>
          <div className="text-red-500 font-mono text-xs sm:text-sm">
            <span className="text-slate-400">INTEL:</span> CONNECTED
          </div>
        </div>
        <div className="flex items-center px-2 py-2 sm:py-0">
          <div className="h-2 bg-gradient-to-r from-red-900 via-red-600 to-red-900 animate-pulse mx-2 sm:mx-4" style={{ width: '60px', maxWidth: '25vw' }}></div>
          <div className="text-red-400 font-mono text-xs px-2 sm:px-4">
            <span className="text-slate-500">UPDATE:</span> {systemStatus.lastUpdate}
          </div>
        </div>
      </div>

      {/* Page Header */}
      <div className="mb-8 sm:mb-12 relative">
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-6">
          <div className="max-w-4xl">
            <div className="relative">
              <h1 className="text-2xl sm:text-4xl font-bold mb-1">
                AlertStrike <span className="text-red-500">SIEM & XDR Operations</span>
              </h1>
              <div className="absolute -top-2 -right-2 w-2 h-2 bg-red-600 rounded-full animate-ping"></div>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-red-400 text-xs sm:text-base mb-4">
              <Shield className="h-4 w-4 sm:h-5 sm:w-5" />
              <span>Security Operations Center</span>
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 hidden sm:block"></span>
              <span className="hidden sm:inline">Incident Response</span>
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 hidden sm:block"></span>
              <span className="hidden sm:inline">Threat Intelligence</span>
            </div>
            <p className="text-slate-300 text-xs sm:text-sm border-l-4 border-red-900/50 pl-3 pr-4 py-2 bg-black/30 rounded-r-md">
              This comprehensive guide applies to all SOC personnel and covers all security alerts and events generated by AlertStrike agents deployed across the organization's infrastructure. It details the specific workflows for leveraging the ShieldX Intel AI platform for indicator enrichment and external intelligence gathering.
            </p>
          </div>
          
          {/* Real-time Stats */}
          <div className="bg-black/40 rounded-xl border border-red-900/30 p-4 backdrop-blur-sm min-w-[280px]">
            <h3 className="text-lg font-bold mb-4 flex items-center">
              <Activity className="h-5 w-5 mr-2 text-red-500" />
              SOC Metrics
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-slate-400 text-sm">Alerts Processed</span>
                <span className="text-white font-mono">{systemStatus.alertsProcessed.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400 text-sm">Active Incidents</span>
                <span className="text-red-400 font-mono">{systemStatus.activeIncidents}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400 text-sm">Avg Response Time</span>
                <span className="text-green-400 font-mono">{systemStatus.averageResponseTime.toFixed(1)}m</span>
              </div>
              <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full w-3/4 bg-gradient-to-r from-red-900 via-red-600 to-red-700 rounded-full relative">
                  <div className="absolute top-0 left-0 h-full w-20 bg-gradient-to-r from-transparent via-red-300/30 to-transparent animate-pulse"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Pills */}
      <div className="flex flex-wrap gap-2 mb-6 sm:mb-8">
        {navigationSections.map((section) => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`flex items-center px-3 py-2 rounded-lg border transition-all text-sm ${
              activeSection === section.id
                ? 'bg-red-900/20 border-red-600/50 text-white'
                : 'bg-slate-900/50 border-slate-800 text-slate-400 hover:bg-red-900/10 hover:border-red-900/30'
            }`}
          >
            {section.icon}
            <span className="ml-2">{section.label}</span>
          </button>
        ))}
      </div>

      {/* Content Sections */}
      <div className="space-y-8">
        {/* Overview Section */}
        {activeSection === 'overview' && (
          <div className="bg-slate-950/70 border border-slate-800 rounded-lg p-6 backdrop-blur-sm">
            <h2 className="text-xl font-bold mb-4 flex items-center">
              <BookOpen className="h-5 w-5 mr-2 text-red-500" />
              AlertStrike SIEM & XDR Overview
            </h2>
            
            <div className="bg-black/40 p-4 rounded-lg border border-slate-800">
              <h3 className="font-bold text-red-400 mb-3 flex items-center">
                <Brain className="h-4 w-4 mr-2" />
                AlertStrike SIEM, XDR, NDR & MDR Capabilities
              </h3>
              <div className="mb-6">
                <p className="text-sm text-slate-300 mb-2">
                  <span className="text-red-400 font-semibold">AlertStrike</span> provides a comprehensive security platform combining SIEM, XDR, NDR, and MDR capabilities with advanced AI integration and extensive platform support.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* SIEM Core Capabilities */}
                <div className="bg-slate-900/60 p-3 rounded border border-slate-800">
                  <h4 className="font-semibold text-white mb-2 flex items-center">
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                    Log Management & Analysis
                  </h4>
                  <p className="text-sm text-slate-300">Centralized collection, parsing, and real-time analysis of security logs from 2400+ data sources including firewalls, endpoints, cloud platforms, and applications.</p>
                </div>
                
                <div className="bg-slate-900/60 p-3 rounded border border-slate-800">
                  <h4 className="font-semibold text-white mb-2 flex items-center">
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                    AI-Powered Threat Detection
                  </h4>
                  <p className="text-sm text-slate-300">Advanced correlation engine with machine learning algorithms, behavioral analytics, and MITRE ATT&CK framework mapping for immediate threat identification.</p>
                </div>
                
                <div className="bg-slate-900/60 p-3 rounded border border-slate-800">
                  <h4 className="font-semibold text-white mb-2 flex items-center">
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                    Compliance & Reporting
                  </h4>
                  <p className="text-sm text-slate-300">Automated compliance reporting for GDPR, HIPAA, PCI-DSS, HIPPA and NIST and with dedicated dashboards and executive reports.</p>
                </div>
                
                {/* XDR Capabilities */}
                <div className="bg-slate-900/60 p-3 rounded border border-slate-800">
                  <h4 className="font-semibold text-white mb-2 flex items-center">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                    Extended Detection & Response
                  </h4>
                  <p className="text-sm text-slate-300">Unified security across endpoints, networks, servers, cloud workloads, email, and identity systems with cross-domain threat correlation.</p>
                </div>
                
                <div className="bg-slate-900/60 p-3 rounded border border-slate-800">
                  <h4 className="font-semibold text-white mb-2 flex items-center">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                    Endpoint Vulnerability Detection
                  </h4>
                  <p className="text-sm text-slate-300">AI-powered CVE detection with automated vulnerability scanning, asset-based risk assessment, and prioritized remediation recommendations.</p>
                </div>
                
                <div className="bg-slate-900/60 p-3 rounded border border-slate-800">
                  <h4 className="font-semibold text-white mb-2 flex items-center">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                    File Integrity Monitoring
                  </h4>
                  <p className="text-sm text-slate-300">Real-time monitoring of critical system files, registry keys, and configuration changes with automated baseline comparisons and alert generation.</p>
                </div>
                
                {/* NDR Capabilities */}
                <div className="bg-slate-900/60 p-3 rounded border border-slate-800">
                  <h4 className="font-semibold text-white mb-2 flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                    Network Detection & Response
                  </h4>
                  <p className="text-sm text-slate-300">Deep packet inspection, network traffic analysis, lateral movement detection, and encrypted traffic analysis with ML-based anomaly detection.</p>
                </div>
                
                <div className="bg-slate-900/60 p-3 rounded border border-slate-800">
                  <h4 className="font-semibold text-white mb-2 flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                    Network Forensics & Analytics
                  </h4>
                  <p className="text-sm text-slate-300">Full packet capture, traffic reconstruction, flow analysis, and network forensics with historical data retention for incident investigation.</p>
                </div>
                
                <div className="bg-slate-900/60 p-3 rounded border border-slate-800">
                  <h4 className="font-semibold text-white mb-2 flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                    Intrusion Detection System
                  </h4>
                  <p className="text-sm text-slate-300">Host-based and network-based intrusion detection with signature-based and behavioral analysis, including rootkit and malware detection.</p>
                </div>
                
                {/* MDR Capabilities */}
                <div className="bg-slate-900/60 p-3 rounded border border-slate-800">
                  <h4 className="font-semibold text-white mb-2 flex items-center">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                    Managed Detection & Response
                  </h4>
                  <p className="text-sm text-slate-300">24/7 SOC services with expert analysts, proactive threat hunting, incident response, and managed security services with SLA guarantees.</p>
                </div>
                
                <div className="bg-slate-900/60 p-3 rounded border border-slate-800">
                  <h4 className="font-semibold text-white mb-2 flex items-center">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                    Automated Response & Remediation
                  </h4>
                  <p className="text-sm text-slate-300">SOAR integration with automated playbooks, active response capabilities, endpoint isolation, and automated threat containment actions.</p>
                </div>
                
                <div className="bg-slate-900/60 p-3 rounded border border-slate-800">
                  <h4 className="font-semibold text-white mb-2 flex items-center">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                    Threat Intelligence Integration
                  </h4>
                  <p className="text-sm text-slate-300">Integration with global threat feeds, dark web monitoring, IOC databases, and threat intelligence platforms for enhanced detection capabilities.</p>
                </div>
                
                {/* Advanced Features */}
                <div className="bg-slate-900/60 p-3 rounded border border-slate-800">
                  <h4 className="font-semibold text-white mb-2 flex items-center">
                    <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                    VMware ESXi Integration
                  </h4>
                  <p className="text-sm text-slate-300">Deep vSphere integration, virtual machine monitoring, hypervisor security, VM lifecycle tracking, and virtual infrastructure protection.</p>
                </div>
                
                <div className="bg-slate-900/60 p-3 rounded border border-slate-800">
                  <h4 className="font-semibold text-white mb-2 flex items-center">
                    <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                    Multi-Cloud Security
                  </h4>
                  <p className="text-sm text-slate-300">AWS, Azure, GCP integration with container security (Docker, Kubernetes), serverless monitoring, and cloud workload protection platforms (CWPP).</p>
                </div>
                
                <div className="bg-slate-900/60 p-3 rounded border border-slate-800">
                  <h4 className="font-semibold text-white mb-2 flex items-center">
                    <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                    Asset Discovery & Management
                  </h4>
                  <p className="text-sm text-slate-300">Comprehensive asset discovery, inventory management, risk scoring, configuration assessment, and asset-based security recommendations.</p>
                </div>
                
                <div className="bg-slate-900/60 p-3 rounded border border-slate-800">
                  <h4 className="font-semibold text-white mb-2 flex items-center">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                    Security Orchestration (SOAR)
                  </h4>
                  <p className="text-sm text-slate-300">Workflow automation, security playbooks, case management, incident tracking, and integration with 100+ security tools and platforms.</p>
                </div>
                
                <div className="bg-slate-900/60 p-3 rounded border border-slate-800">
                  <h4 className="font-semibold text-white mb-2 flex items-center">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                    Advanced Threat Hunting
                  </h4>
                  <p className="text-sm text-slate-300">Proactive threat hunting capabilities with custom queries, behavioral analytics, threat hypothesis testing, and IOC hunting across the environment.</p>
                </div>
                
                <div className="bg-slate-900/60 p-3 rounded border border-slate-800">
                  <h4 className="font-semibold text-white mb-2 flex items-center">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                    AI-Enhanced CVE Management
                  </h4>
                  <p className="text-sm text-slate-300">Automated CVE detection, impact assessment, exploit availability checking, and AI-driven patch prioritization with business context awareness.</p>
                </div>
              </div>

              <div className="mt-6 bg-black/40 p-4 rounded-lg border border-slate-800">
                <span className="font-bold text-yellow-400">Real-time Security Monitoring</span>
                <p className="text-sm text-slate-300 my-2">24/7 continuous monitoring with real-time alerting, automated correlation, and instant threat detection across all infrastructure components.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center"><span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>Log Aggregation & Correlation</div>
                  <div className="flex items-center"><span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>Custom Alert Rules</div>
                  <div className="flex items-center"><span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>Dashboard Analytics</div>
                  <div className="flex items-center"><span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>Event Correlation Engine</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Roles & Responsibilities Section */}
        {activeSection === 'roles' && (
          <div className="bg-slate-950/70 border border-slate-800 rounded-lg p-6 backdrop-blur-sm">
            <h2 className="text-xl font-bold mb-6 flex items-center">
              <Users className="h-5 w-5 mr-2 text-red-500" />
              Roles and Responsibilities
            </h2>
            
            <div className="space-y-6">
              {/* L1 Analyst */}
              <div className="bg-black/40 rounded-lg p-5 border border-blue-600/30">
                <h3 className="text-lg font-bold text-blue-400 mb-3 flex items-center">
                  <Target className="h-5 w-5 mr-2" />
                  L1 Analyst (Triage Specialist)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-white mb-2">Primary Responsibilities:</h4>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li className="flex items-start"><ArrowRight className="h-3 w-3 mr-2 mt-0.5 text-blue-500 flex-shrink-0" />Monitors AlertStrike Dashboard for incoming alerts</li>
                      <li className="flex items-start"><ArrowRight className="h-3 w-3 mr-2 mt-0.5 text-blue-500 flex-shrink-0" />Performs initial analysis and triage (TP vs FP)</li>
                      <li className="flex items-start"><ArrowRight className="h-3 w-3 mr-2 mt-0.5 text-blue-500 flex-shrink-0" />Uses ShieldX Intel AI for rapid IOC enrichment</li>
                      <li className="flex items-start"><ArrowRight className="h-3 w-3 mr-2 mt-0.5 text-blue-500 flex-shrink-0" />Escalates validated incidents to L2 Analysts</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-white mb-2">Tools & Access:</h4>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li className="flex items-start"><ArrowRight className="h-3 w-3 mr-2 mt-0.5 text-blue-500 flex-shrink-0" />AlertStrike Security Events Dashboard</li>
                      <li className="flex items-start"><ArrowRight className="h-3 w-3 mr-2 mt-0.5 text-blue-500 flex-shrink-0" />ShieldX Intel AI platform</li>
                      <li className="flex items-start"><ArrowRight className="h-3 w-3 mr-2 mt-0.5 text-blue-500 flex-shrink-0" />Incident management system</li>
                      <li className="flex items-start"><ArrowRight className="h-3 w-3 mr-2 mt-0.5 text-blue-500 flex-shrink-0" />Basic network and endpoint visibility</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* L2 Analyst */}
              <div className="bg-black/40 rounded-lg p-5 border border-orange-600/30">
                <h3 className="text-lg font-bold text-orange-400 mb-3 flex items-center">
                  <Search className="h-5 w-5 mr-2" />
                  L2 Analyst (Incident Responder)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-white mb-2">Primary Responsibilities:</h4>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li className="flex items-start"><ArrowRight className="h-3 w-3 mr-2 mt-0.5 text-orange-500 flex-shrink-0" />Conducts in-depth investigation of escalated incidents</li>
                      <li className="flex items-start"><ArrowRight className="h-3 w-3 mr-2 mt-0.5 text-orange-500 flex-shrink-0" />Performs host-based forensics using AlertStrike</li>
                      <li className="flex items-start"><ArrowRight className="h-3 w-3 mr-2 mt-0.5 text-orange-500 flex-shrink-0" />Utilizes Intel AI for deeper context and CVE analysis</li>
                      <li className="flex items-start"><ArrowRight className="h-3 w-3 mr-2 mt-0.5 text-orange-500 flex-shrink-0" />Implements containment and eradication procedures</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-white mb-2">Advanced Capabilities:</h4>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li className="flex items-start"><ArrowRight className="h-3 w-3 mr-2 mt-0.5 text-orange-500 flex-shrink-0" />File Integrity Monitoring (FIM)</li>
                      <li className="flex items-start"><ArrowRight className="h-3 w-3 mr-2 mt-0.5 text-orange-500 flex-shrink-0" />System Check (Syscheck) analysis</li>
                      <li className="flex items-start"><ArrowRight className="h-3 w-3 mr-2 mt-0.5 text-orange-500 flex-shrink-0" />Command log forensics</li>
                      <li className="flex items-start"><ArrowRight className="h-3 w-3 mr-2 mt-0.5 text-orange-500 flex-shrink-0" />AlertStrike Active Response</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* L3 Analyst */}
              <div className="bg-black/40 rounded-lg p-5 border border-red-600/30">
                <h3 className="text-lg font-bold text-red-400 mb-3 flex items-center">
                  <Eye className="h-5 w-5 mr-2" />
                  L3 Analyst (Threat Hunter / SME)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-white mb-2">Expert Responsibilities:</h4>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li className="flex items-start"><ArrowRight className="h-3 w-3 mr-2 mt-0.5 text-red-500 flex-shrink-0" />Proactive threat hunting within AlertStrike logs</li>
                      <li className="flex items-start"><ArrowRight className="h-3 w-3 mr-2 mt-0.5 text-red-500 flex-shrink-0" />Uses Intel AI news aggregation for hunting hypotheses</li>
                      <li className="flex items-start"><ArrowRight className="h-3 w-3 mr-2 mt-0.5 text-red-500 flex-shrink-0" />Develops new custom rules and decoders</li>
                      <li className="flex items-start"><ArrowRight className="h-3 w-3 mr-2 mt-0.5 text-red-500 flex-shrink-0" />Final escalation point for complex incidents</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-white mb-2">Advanced Tools:</h4>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li className="flex items-start"><ArrowRight className="h-3 w-3 mr-2 mt-0.5 text-red-500 flex-shrink-0" />Intel AI generative AI features</li>
                      <li className="flex items-start"><ArrowRight className="h-3 w-3 mr-2 mt-0.5 text-red-500 flex-shrink-0" />Rule creation assistance features</li>
                      <li className="flex items-start"><ArrowRight className="h-3 w-3 mr-2 mt-0.5 text-red-500 flex-shrink-0" />Advanced correlation and analytics</li>
                      <li className="flex items-start"><ArrowRight className="h-3 w-3 mr-2 mt-0.5 text-red-500 flex-shrink-0" />Threat intelligence research</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Alert Severities Section */}
        {activeSection === 'severities' && (
          <div className="bg-slate-950/70 border border-slate-800 rounded-lg p-6 backdrop-blur-sm">
            <h2 className="text-xl font-bold mb-6 flex items-center">
              <AlertTriangle className="h-5 w-5 mr-2 text-red-500" />
              Alert Severity Levels & Response Procedures
            </h2>
            
            <div className="space-y-4">
              {Object.entries(severityLevels).map(([key, severity]) => (
                <div key={key} className={`rounded-lg border ${severity.borderColor} ${severity.bgColor} overflow-hidden`}>
                  <div
                    className="p-4 cursor-pointer flex items-center justify-between hover:bg-black/20 transition-colors"
                    onClick={() => setExpandedSeverity(expandedSeverity === key ? null : key)}
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`p-2 rounded-lg border ${severity.borderColor} ${severity.color}`}>
                        {severity.icon}
                      </div>
                      <div>
                        <div className="flex items-center space-x-3">
                          <h3 className={`text-lg font-bold ${severity.color}`}>{severity.level}</h3>
                          <span className="text-sm text-slate-400">Rule Level {severity.range}</span>
                          <span className="text-xs bg-black/40 px-2 py-1 rounded border border-slate-700">
                            Response: {severity.responseTime}
                          </span>
                        </div>
                        <p className="text-sm text-slate-300 mt-1">{severity.description}</p>
                      </div>
                    </div>
                    {expandedSeverity === key ? (
                      <ChevronDown className="h-5 w-5 text-slate-400" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-slate-400" />
                    )}
                  </div>
                  
                  {expandedSeverity === key && (
                    <div className="border-t border-slate-800/50 bg-black/40 p-6">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-bold text-white mb-3 flex items-center">
                            <Clock className="h-4 w-4 mr-2 text-blue-500" />
                            Response Timeline
                          </h4>
                          {key === 'critical' && (
                            <div className="space-y-3">
                              <div className="bg-red-900/20 p-3 rounded border border-red-800/30">
                                <div className="font-medium text-red-400">0-5 minutes: Immediate Actions</div>
                                <ul className="text-sm text-slate-300 mt-2 space-y-1">
                                  <li>• Acknowledge alert and create high-priority incident</li>
                                  <li>• Notify L2/L3 analysts and management immediately</li>
                                  <li>• Begin initial containment if possible</li>
                                </ul>
                              </div>
                              <div className="bg-orange-900/20 p-3 rounded border border-orange-800/30">
                                <div className="font-medium text-orange-400">5-15 minutes: Assessment</div>
                                <ul className="text-sm text-slate-300 mt-2 space-y-1">
                                  <li>• Extract and analyze IOCs using ShieldX Intel AI</li>
                                  <li>• Determine scope and potential impact</li>
                                  <li>• Initiate immediate containment measures</li>
                                </ul>
                              </div>
                              <div className="bg-yellow-900/20 p-3 rounded border border-yellow-800/30">
                                <div className="font-medium text-yellow-400">15+ minutes: Response</div>
                                <ul className="text-sm text-slate-300 mt-2 space-y-1">
                                  <li>• Execute incident response procedures</li>
                                  <li>• Begin forensic collection and analysis</li>
                                  <li>• Coordinate with relevant stakeholders</li>
                                </ul>
                              </div>
                            </div>
                          )}
                          {key === 'high' && (
                            <div className="space-y-3">
                              <div className="bg-orange-900/20 p-3 rounded border border-orange-800/30">
                                <div className="font-medium text-orange-400">0-15 minutes: Initial Triage</div>
                                <ul className="text-sm text-slate-300 mt-2 space-y-1">
                                  <li>• Acknowledge alert and assess criticality</li>
                                  <li>• Extract IOCs and perform Intel AI lookup</li>
                                  <li>• Determine if escalation to L2 is needed</li>
                                </ul>
                              </div>
                              <div className="bg-yellow-900/20 p-3 rounded border border-yellow-800/30">
                                <div className="font-medium text-yellow-400">15-30 minutes: Investigation</div>
                                <ul className="text-sm text-slate-300 mt-2 space-y-1">
                                  <li>• Conduct detailed analysis of the event</li>
                                  <li>• Check for related events and patterns</li>
                                  <li>• Begin containment if threat is confirmed</li>
                                </ul>
                              </div>
                            </div>
                          )}
                          {(key === 'medium' || key === 'low') && (
                            <div className="space-y-3">
                              <div className="bg-blue-900/20 p-3 rounded border border-blue-800/30">
                                <div className="font-medium text-blue-400">Standard Investigation Process</div>
                                <ul className="text-sm text-slate-300 mt-2 space-y-1">
                                  <li>• Follow standard triage procedures</li>
                                  <li>• Use ShieldX Intel AI for IOC analysis</li>
                                  <li>• Document findings and determine next steps</li>
                                  <li>• Monitor for recurring patterns</li>
                                </ul>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div>
                          <h4 className="font-bold text-white mb-3 flex items-center">
                            <Users className="h-4 w-4 mr-2 text-green-500" />
                            Escalation & Communication
                          </h4>
                          <div className="bg-black/60 p-4 rounded border border-slate-800">
                            <div className="mb-3">
                              <span className="text-sm text-slate-400">Escalation Path:</span>
                              <p className="text-sm text-white">{severity.escalation}</p>
                            </div>
                            
                            {key === 'critical' && (
                              <div className="space-y-2">
                                <div className="text-xs bg-red-900/30 px-2 py-1 rounded text-red-300 border border-red-800/30">
                                  <strong>Management Notification:</strong> CISO, IT Director within 15 minutes
                                </div>
                                <div className="text-xs bg-orange-900/30 px-2 py-1 rounded text-orange-300 border border-orange-800/30">
                                  <strong>L2/L3 Engagement:</strong> Immediate mobilization required
                                </div>
                                <div className="text-xs bg-blue-900/30 px-2 py-1 rounded text-blue-300 border border-blue-800/30">
                                  <strong>External:</strong> Consider law enforcement/external IR firm
                                </div>
                              </div>
                            )}
                            
                            {key === 'high' && (
                              <div className="space-y-2">
                                <div className="text-xs bg-orange-900/30 px-2 py-1 rounded text-orange-300 border border-orange-800/30">
                                  <strong>L2 Escalation:</strong> Within 30 minutes if confirmed threat
                                </div>
                                <div className="text-xs bg-blue-900/30 px-2 py-1 rounded text-blue-300 border border-blue-800/30">
                                  <strong>Management:</strong> Notify within 1 hour of confirmation
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Standard Procedures Section */}
        {activeSection === 'procedures' && (
          <div className="bg-slate-950/70 border border-slate-800 rounded-lg p-6 backdrop-blur-sm">
            <h2 className="text-xl font-bold mb-6 flex items-center">
              <FileText className="h-5 w-5 mr-2 text-red-500" />
              Standard Operating Procedures (SOPs)
            </h2>
            
            <div className="space-y-4">
              {sopProcedures.map((sop) => (
                <div key={sop.id} className="border border-slate-800 rounded-lg overflow-hidden bg-black/40">
                  <div
                    className="p-4 cursor-pointer flex items-center justify-between hover:bg-slate-900/50 transition-colors"
                    onClick={() => setExpandedSOP(expandedSOP === sop.id ? null : sop.id)}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="p-2 rounded-lg border border-slate-700 bg-black/60">
                        {sop.icon}
                      </div>
                      <div>
                        <div className="flex items-center space-x-3">
                          <h3 className="text-lg font-bold text-white">{sop.title}</h3>
                          <span className="text-xs bg-slate-800 px-2 py-1 rounded text-slate-300">{sop.level}</span>
                        </div>
                        <p className="text-sm text-slate-400 mt-1">{sop.description}</p>
                      </div>
                    </div>
                    {expandedSOP === sop.id ? (
                      <ChevronDown className="h-5 w-5 text-slate-400" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-slate-400" />
                    )}
                  </div>
                  
                  {expandedSOP === sop.id && (
                    <div className="border-t border-slate-800/50 bg-black/60 p-6">
                      {/* SOP Content based on ID */}
                      {sop.id === 'sop1' && (
                        <div className="space-y-6">
                          <div className="bg-blue-900/20 border border-blue-800/30 rounded-lg p-4">
                            <h4 className="font-bold text-blue-400 mb-3">Procedure Overview</h4>
                            <p className="text-sm text-slate-300 mb-4">
                              This procedure begins when a new alert with a rule level of 7 or higher appears in the AlertStrike Dashboard.
                            </p>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                              <h4 className="font-bold text-white flex items-center">
                                <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                                Step 1: Acknowledge the Alert
                              </h4>
                              <ul className="text-sm text-slate-300 space-y-2 ml-6">
                                <li>• Navigate to Security Events dashboard in AlertStrike</li>
                                <li>• Acknowledge the highest priority, unassigned alert</li>
                                <li>• Open corresponding ticket in incident management system</li>
                              </ul>

                              <h4 className="font-bold text-white flex items-center">
                                <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                                Step 2: Initial Alert Review (5 W's)
                              </h4>
                              <ul className="text-sm text-slate-300 space-y-2 ml-6">
                                <li><strong>Who:</strong> Which agent/endpoint? (agent.name, agent.ip)</li>
                                <li><strong>What:</strong> Alert description and rule ID? (rule.description, rule.id)</li>
                                <li><strong>When:</strong> Event timestamp? (@timestamp)</li>
                                <li><strong>Where:</strong> Log source or file? (location)</li>
                                <li><strong>Why:</strong> MITRE ATT&CK® association? (mitre.tactic, mitre.technique)</li>
                              </ul>
                            </div>

                            <div className="space-y-4">
                              <h4 className="font-bold text-white flex items-center">
                                <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                                Step 3: Extract IOCs
                              </h4>
                              <div className="bg-black/60 rounded p-3 border border-slate-800">
                                <p className="text-sm text-slate-400 mb-2">Key indicators to extract:</p>
                                <ul className="text-xs text-slate-300 space-y-1">
                                  <li>• IP Addresses (source/destination)</li>
                                  <li>• File Hashes (MD5, SHA1, SHA256)</li>
                                  <li>• Domain Names / URLs</li>
                                  <li>• Usernames</li>
                                  <li>• Filenames or paths</li>
                                </ul>
                              </div>

                              <h4 className="font-bold text-white flex items-center">
                                <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                                Step 4: Intel AI Analysis
                              </h4>
                              <div className="bg-purple-900/20 border border-purple-800/30 rounded p-3">
                                <p className="text-sm text-purple-300 mb-2">Input IOCs into Intel AI modules:</p>
                                <ul className="text-xs text-slate-300 space-y-1">
                                  <li>• VirusTotal, AbuseIPDB, ThreatFox</li>
                                  <li>• Shodan, Pulsedive, CrowdSec</li>
                                  <li>• Review reputation scores and detections</li>
                                </ul>
                              </div>

                              <h4 className="font-bold text-white flex items-center">
                                <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                                Step 5: Determine Fidelity & Action
                              </h4>
                              <div className="grid grid-cols-2 gap-2">
                                <div className="bg-green-900/20 border border-green-800/30 rounded p-2">
                                  <div className="text-xs font-bold text-green-400 mb-1">True Positive</div>
                                  <div className="text-xs text-slate-300">Escalate to L2 with summary</div>
                                </div>
                                <div className="bg-yellow-900/20 border border-yellow-800/30 rounded p-2">
                                  <div className="text-xs font-bold text-yellow-400 mb-1">False Positive</div>
                                  <div className="text-xs text-slate-300">Document reasoning, close ticket</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {sop.id === 'sop2' && (
                        <div className="space-y-6">
                          <div className="bg-orange-900/20 border border-orange-800/30 rounded-lg p-4">
                            <h4 className="font-bold text-orange-400 mb-3">Deep Dive Investigation Process</h4>
                            <p className="text-sm text-slate-300">
                              This procedure begins when an L1 Analyst escalates a validated incident for in-depth analysis.
                            </p>
                          </div>

                          <div className="space-y-6">
                            <div>
                              <h4 className="font-bold text-white mb-3 flex items-center">
                                <Search className="h-4 w-4 mr-2 text-orange-500" />
                                Investigation Workflow
                              </h4>
                              
                              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                <div className="bg-black/60 rounded-lg p-4 border border-slate-800">
                                  <h5 className="font-medium text-orange-400 mb-3">1. Review & Correlate</h5>
                                  <ul className="text-sm text-slate-300 space-y-2">
                                    <li>• Review L1 escalation notes</li>
                                    <li>• Use agent.id to pivot to agent view</li>
                                    <li>• Look for related events (before/after)</li>
                                    <li>• Understand attack chain progression</li>
                                  </ul>
                                </div>
                                
                                <div className="bg-black/60 rounded-lg p-4 border border-slate-800">
                                  <h5 className="font-medium text-orange-400 mb-3">2. Host Analysis</h5>
                                  <ul className="text-sm text-slate-300 space-y-2">
                                    <li>• Check FIM for unauthorized changes</li>
                                    <li>• Review Vulnerability Detector findings</li>
                                    <li>• Analyze process/command auditing</li>
                                    <li>• Examine system integrity</li>
                                  </ul>
                                </div>
                                
                                <div className="bg-black/60 rounded-lg p-4 border border-slate-800">
                                  <h5 className="font-medium text-orange-400 mb-3">3. Advanced Intel AI</h5>
                                  <ul className="text-sm text-slate-300 space-y-2">
                                    <li>• CVE analysis and exploit research</li>
                                    <li>• Email/URL analysis if applicable</li>
                                    <li>• Generative AI threat profiling</li>
                                    <li>• MITRE ATT&CK technique research</li>
                                  </ul>
                                </div>
                              </div>
                            </div>

                            <div className="bg-red-900/10 border border-red-800/30 rounded-lg p-4">
                              <h4 className="font-bold text-red-400 mb-3">Advanced Intel AI Queries</h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-black/40 rounded p-3 border border-slate-800">
                                  <div className="text-sm text-slate-300 mb-2"><strong>Example Prompts:</strong></div>
                                  <div className="text-xs text-slate-400 space-y-1">
                                    <p>"Summarize threat profile of IP with multiple high-risk detections"</p>
                                    <p>"Based on this rule and file hash, identify likely malware family"</p>
                                    <p>"Explain MITRE technique T1059.003 context"</p>
                                  </div>
                                </div>
                                <div className="bg-black/40 rounded p-3 border border-slate-800">
                                  <div className="text-sm text-slate-300 mb-2"><strong>Timeline Development:</strong></div>
                                  <div className="text-xs text-slate-400 space-y-1">
                                    <p>• Construct detailed incident timeline</p>
                                    <p>• Use AlertStrike and Intel AI timestamps</p>
                                    <p>• Document attack progression</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {sop.id === 'sop3' && (
                        <div className="space-y-6">
                          <div className="bg-red-900/20 border border-red-800/30 rounded-lg p-4">
                            <h4 className="font-bold text-red-400 mb-3">Incident Response & Recovery</h4>
                            <p className="text-sm text-slate-300">
                              Systematic approach to containing, eradicating, and recovering from security incidents.
                            </p>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-black/60 rounded-lg p-4 border border-red-800/30">
                              <h4 className="font-bold text-red-400 mb-3 flex items-center">
                                <Lock className="h-4 w-4 mr-2" />
                                Containment
                              </h4>
                              <div className="space-y-3">
                                <div className="text-sm text-slate-300">
                                  <strong>AlertStrike Active Response:</strong>
                                </div>
                                <ul className="text-sm text-slate-400 space-y-1">
                                  <li>• Block malicious source IP (firewall-drop)</li>
                                  <li>• Isolate infected endpoint from network</li>
                                  <li>• Terminate malicious processes</li>
                                  <li>• Verify containment success</li>
                                </ul>
                                
                                <div className="bg-red-900/30 rounded p-2 border border-red-800/40">
                                  <div className="text-xs text-red-300"><strong>Critical:</strong> Confirm action success via endpoint status and logs</div>
                                </div>
                              </div>
                            </div>
                            
                            <div className="bg-black/60 rounded-lg p-4 border border-orange-800/30">
                              <h4 className="font-bold text-orange-400 mb-3 flex items-center">
                                <Zap className="h-4 w-4 mr-2" />
                                Eradication
                              </h4>
                              <div className="space-y-3">
                                <ul className="text-sm text-slate-300 space-y-2">
                                  <li>• Remove malicious files and artifacts</li>
                                  <li>• Eliminate persistence mechanisms</li>
                                  <li>• Clean registry entries and scheduled tasks</li>
                                  <li>• Patch vulnerabilities that enabled breach</li>
                                </ul>
                                
                                <div className="bg-orange-900/30 rounded p-2 border border-orange-800/40">
                                  <div className="text-xs text-orange-300"><strong>Note:</strong> May require manual intervention or additional security tools</div>
                                </div>
                              </div>
                            </div>
                            
                            <div className="bg-black/60 rounded-lg p-4 border border-green-800/30">
                              <h4 className="font-bold text-green-400 mb-3 flex items-center">
                                <Activity className="h-4 w-4 mr-2" />
                                Recovery
                              </h4>
                              <div className="space-y-3">
                                <ul className="text-sm text-slate-300 space-y-2">
                                  <li>• Restore systems to known-good state</li>
                                  <li>• Rebuild from clean backups if necessary</li>
                                  <li>• Update security controls and monitoring</li>
                                  <li>• Enhanced monitoring post-incident</li>
                                </ul>
                                
                                <div className="bg-green-900/30 rounded p-2 border border-green-800/40">
                                  <div className="text-xs text-green-300"><strong>Monitor:</strong> Use AlertStrike to ensure complete threat removal</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {sop.id === 'sop4' && (
                        <div className="space-y-6">
                          <div className="bg-purple-900/20 border border-purple-800/30 rounded-lg p-4">
                            <h4 className="font-bold text-purple-400 mb-3">Proactive Threat Hunting</h4>
                            <p className="text-sm text-slate-300">
                              Advanced procedures for proactive threat detection and hunt hypothesis development.
                            </p>
                          </div>

                          <div className="space-y-6">
                            <div>
                              <h4 className="font-bold text-white mb-4 flex items-center">
                                <Brain className="h-4 w-4 mr-2 text-purple-500" />
                                Hunting Methodology
                              </h4>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                  <div className="bg-black/60 rounded-lg p-4 border border-slate-800">
                                    <h5 className="font-medium text-purple-400 mb-3">1. Hypothesis Formation</h5>
                                    <ul className="text-sm text-slate-300 space-y-2">
                                      <li>• Use Intel AI news aggregation</li>
                                      <li>• Research new TTPs and threat campaigns</li>
                                      <li>• Identify industry-relevant threats</li>
                                      <li>• Formulate testable hypotheses</li>
                                    </ul>
                                    
                                    <div className="mt-3 bg-purple-900/30 rounded p-2 border border-purple-800/40">
                                      <div className="text-xs text-purple-300"><strong>Example:</strong> "FIN7 using certutil.exe LOLBAS technique for payload downloads"</div>
                                    </div>
                                  </div>

                                  <div className="bg-black/60 rounded-lg p-4 border border-slate-800">
                                    <h5 className="font-medium text-purple-400 mb-3">2. Hunt Execution</h5>
                                    <ul className="text-sm text-slate-300 space-y-2">
                                      <li>• Navigate to AlertStrike Discover tab</li>
                                      <li>• Create custom search queries</li>
                                      <li>• Look for statistical outliers</li>
                                      <li>• Analyze process relationships</li>
                                    </ul>
                                    
                                    <div className="mt-3 bg-slate-900/60 rounded p-2 border border-slate-700">
                                      <div className="text-xs text-slate-400 font-mono">
                                        rule.groups:"ossec" AND process.name:"certutil.exe" AND process.command_line:(*-urlcache* OR *-f* OR *-split*)
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="space-y-4">
                                  <div className="bg-black/60 rounded-lg p-4 border border-slate-800">
                                    <h5 className="font-medium text-purple-400 mb-3">3. Investigation & Enrichment</h5>
                                    <ul className="text-sm text-slate-300 space-y-2">
                                      <li>• Follow SOP 2 for suspicious findings</li>
                                      <li>• Use Intel AI for IOC enrichment</li>
                                      <li>• Correlate across multiple data sources</li>
                                      <li>• Document findings and evidence</li>
                                    </ul>
                                  </div>

                                  <div className="bg-black/60 rounded-lg p-4 border border-slate-800">
                                    <h5 className="font-medium text-purple-400 mb-3">4. Detection Improvement</h5>
                                    <ul className="text-sm text-slate-300 space-y-2">
                                      <li>• Identify detection gaps</li>
                                      <li>• Use Intel AI rule creation feature</li>
                                      <li>• Test and deploy new rules</li>
                                      <li>• Document hunting results</li>
                                    </ul>
                                    
                                    <div className="mt-3 bg-green-900/30 rounded p-2 border border-green-800/40">
                                      <div className="text-xs text-green-300"><strong>AI Prompt:</strong> "Create AlertStrike rule for certutil.exe with -urlcache and -f arguments"</div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Detailed Workflows Section */}
        {activeSection === 'workflows' && (
          <div className="bg-slate-950/70 border border-slate-800 rounded-lg p-6 backdrop-blur-sm">
            <h2 className="text-xl font-bold mb-6 flex items-center">
              <Activity className="h-5 w-5 mr-2 text-red-500" />
              Detailed Response Workflows
            </h2>
            
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {/* Critical Alert Workflow */}
              <div className="bg-red-900/10 border border-red-800/30 rounded-lg p-5">
                <h3 className="text-lg font-bold text-red-400 mb-4 flex items-center">
                  <AlertTriangle className="h-5 w-5 mr-2" />
                  Critical Alert Workflow (Level 10-15)
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="bg-red-600 text-white text-xs px-2 py-1 rounded font-bold mt-1">0-5m</div>
                    <div className="flex-1">
                      <div className="font-medium text-white">Immediate Response</div>
                      <ul className="text-sm text-slate-300 space-y-1 mt-1">
                        <li>• Alert acknowledgment & high-priority incident creation</li>
                        <li>• Immediate L2/L3 and management notification</li>
                        <li>• Initial containment assessment</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className="bg-orange-600 text-white text-xs px-2 py-1 rounded font-bold mt-1">5-15m</div>
                    <div className="flex-1">
                      <div className="font-medium text-white">Rapid Analysis</div>
                      <ul className="text-sm text-slate-300 space-y-1 mt-1">
                        <li>• IOC extraction and Intel AI analysis</li>
                        <li>• Scope and impact determination</li>
                        <li>• Containment initiation</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className="bg-yellow-600 text-white text-xs px-2 py-1 rounded font-bold mt-1">15m+</div>
                    <div className="flex-1">
                      <div className="font-medium text-white">Full Response</div>
                      <ul className="text-sm text-slate-300 space-y-1 mt-1">
                        <li>• Execute comprehensive incident response</li>
                        <li>• Forensic collection and detailed analysis</li>
                        <li>• Stakeholder coordination and communication</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              {/* Standard Investigation Workflow */}
              <div className="bg-blue-900/10 border border-blue-800/30 rounded-lg p-5">
                <h3 className="text-lg font-bold text-blue-400 mb-4 flex items-center">
                  <Search className="h-5 w-5 mr-2" />
                  Standard Investigation Workflow
                </h3>
                
                <div className="space-y-4">
                  <div className="bg-black/60 rounded p-3 border border-slate-800">
                    <div className="font-medium text-blue-400 mb-2">Phase 1: Initial Triage</div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="text-sm">
                        <div className="text-slate-400 mb-1">Alert Assessment</div>
                        <ul className="text-xs text-slate-300 space-y-1">
                          <li>• Rule level verification</li>
                          <li>• MITRE mapping check</li>
                          <li>• Asset criticality review</li>
                        </ul>
                      </div>
                      <div className="text-sm">
                        <div className="text-slate-400 mb-1">IOC Collection</div>
                        <ul className="text-xs text-slate-300 space-y-1">
                          <li>• IP addresses extraction</li>
                          <li>• File hash identification</li>
                          <li>• Domain/URL parsing</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-black/60 rounded p-3 border border-slate-800">
                    <div className="font-medium text-blue-400 mb-2">Phase 2: Intelligence Analysis</div>
                    <div className="text-sm text-slate-300">
                      <div className="mb-2">ShieldX Intel AI Modules:</div>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="text-xs bg-purple-900/30 px-2 py-1 rounded border border-purple-800/30">VirusTotal</div>
                        <div className="text-xs bg-purple-900/30 px-2 py-1 rounded border border-purple-800/30">AbuseIPDB</div>
                        <div className="text-xs bg-purple-900/30 px-2 py-1 rounded border border-purple-800/30">ThreatFox</div>
                        <div className="text-xs bg-purple-900/30 px-2 py-1 rounded border border-purple-800/30">Shodan</div>
                        <div className="text-xs bg-purple-900/30 px-2 py-1 rounded border border-purple-800/30">Pulsedive</div>
                        <div className="text-xs bg-purple-900/30 px-2 py-1 rounded border border-purple-800/30">CrowdSec</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-black/60 rounded p-3 border border-slate-800">
                    <div className="font-medium text-blue-400 mb-2">Phase 3: Decision & Action</div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-green-900/30 rounded p-2 border border-green-800/30">
                        <div className="text-xs font-bold text-green-400 mb-1">True Positive</div>
                        <div className="text-xs text-slate-300">Escalate with comprehensive analysis summary</div>
                      </div>
                      <div className="bg-yellow-900/30 rounded p-2 border border-yellow-800/30">
                        <div className="text-xs font-bold text-yellow-400 mb-1">False Positive</div>
                        <div className="text-xs text-slate-300">Document, tune rules if needed, close</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ShieldX Intel AI Integration */}
        {activeSection === 'integration' && (
          <div className="bg-slate-950/70 border border-slate-800 rounded-lg p-6 backdrop-blur-sm">
            <h2 className="text-xl font-bold mb-6 flex items-center">
              <Brain className="h-5 w-5 mr-2 text-red-500" />
              ShieldX Intel AI Integration
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-6">
                <div className="bg-purple-900/20 border border-purple-800/30 rounded-lg p-5">
                  <h3 className="text-lg font-bold text-purple-400 mb-4 flex items-center">
                    <Database className="h-5 w-5 mr-2" />
                    Intelligence Sources
                  </h3>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="bg-black/60 rounded p-3 border border-slate-800">
                      <div className="text-sm font-medium text-white mb-2">Reputation Services</div>
                      <ul className="text-xs text-slate-300 space-y-1">
                        <li>• VirusTotal - File/URL/IP reputation</li>
                        <li>• AbuseIPDB - IP abuse database</li>
                        <li>• URLScan - URL analysis service</li>
                        <li>• Checkphish.ai - Phishing detection</li>
                      </ul>
                    </div>
                    
                    <div className="bg-black/60 rounded p-3 border border-slate-800">
                      <div className="text-sm font-medium text-white mb-2">Threat Intelligence</div>
                      <ul className="text-xs text-slate-300 space-y-1">
                        <li>• ThreatFox - Malware IOCs</li>
                        <li>• CrowdSec - Community intelligence</li>
                        <li>• Pulsedive - OSINT aggregation</li>
                        <li>• Shodan - Internet-wide scanning</li>
                      </ul>
                    </div>
                    
                    <div className="bg-black/60 rounded p-3 border border-slate-800">
                      <div className="text-sm font-medium text-white mb-2">Vulnerability Data</div>
                      <ul className="text-xs text-slate-300 space-y-1">
                        <li>• NIST NVD - CVE database</li>
                        <li>• GitHub - Exploit code search</li>
                        <li>• MITRE ATT&CK - Tactics/Techniques</li>
                        <li>• CISA - Known exploited vulnerabilities</li>
                      </ul>
                    </div>
                    
                    <div className="bg-black/60 rounded p-3 border border-slate-800">
                      <div className="text-sm font-medium text-white mb-2">Communication Analysis</div>
                      <ul className="text-xs text-slate-300 space-y-1">
                        <li>• Emailrep.io - Email reputation</li>
                        <li>• WHOIS - Domain information</li>
                        <li>• DNS - Resolution analysis</li>
                        <li>• Certificate transparency logs</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-green-900/20 border border-green-800/30 rounded-lg p-5">
                  <h3 className="text-lg font-bold text-green-400 mb-4 flex items-center">
                    <Zap className="h-5 w-5 mr-2" />
                    Automation Features
                  </h3>
                  
                  <div className="space-y-3">
                    <div className="bg-black/60 rounded p-3 border border-slate-800">
                      <div className="text-sm font-medium text-white mb-2">Automated Enrichment</div>
                      <p className="text-xs text-slate-300 mb-2">
                        Submit IOCs to multiple sources simultaneously and receive consolidated intelligence reports.
                      </p>
                      <div className="text-xs bg-slate-900/60 p-2 rounded border border-slate-700 font-mono">
                        Input: 192.168.1.100 → Output: Consolidated threat report from 6+ sources
                      </div>
                    </div>
                    
                    <div className="bg-black/60 rounded p-3 border border-slate-800">
                      <div className="text-sm font-medium text-white mb-2">Rule Generation Assistance</div>
                      <p className="text-xs text-slate-300 mb-2">
                        AI-powered AlertStrike rule creation based on identified threats and attack patterns.
                      </p>
                      <div className="text-xs bg-slate-900/60 p-2 rounded border border-slate-700 font-mono">
                        "Create rule for suspicious certutil.exe usage" → Generated AlertStrike rule
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="bg-blue-900/20 border border-blue-800/30 rounded-lg p-5">
                  <h3 className="text-lg font-bold text-blue-400 mb-4 flex items-center">
                    <Brain className="h-5 w-5 mr-2" />
                    AI-Powered Analysis
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="bg-black/60 rounded p-3 border border-slate-800">
                      <div className="text-sm font-medium text-white mb-2">Threat Profiling</div>
                      <div className="text-xs text-slate-300 mb-2">Example AI queries for threat analysis:</div>
                      <div className="space-y-1">
                        <div className="text-xs bg-slate-900/60 p-2 rounded border border-slate-700">
                          "Summarize the threat profile of IP 1.2.3.4 with multiple high-risk detections"
                        </div>
                        <div className="text-xs bg-slate-900/60 p-2 rounded border border-slate-700">
                          "Based on this AlertStrike rule and file hash, what is the likely malware family?"
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-black/60 rounded p-3 border border-slate-800">
                      <div className="text-sm font-medium text-white mb-2">Contextual Intelligence</div>
                      <div className="text-xs text-slate-300 mb-2">Advanced threat context and guidance:</div>
                      <div className="space-y-1">
                        <div className="text-xs bg-slate-900/60 p-2 rounded border border-slate-700">
                          "Explain MITRE ATT&CK technique T1059.003 and detection strategies"
                        </div>
                        <div className="text-xs bg-slate-900/60 p-2 rounded border border-slate-700">
                          "What are the latest APT campaigns targeting our industry sector?"
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-orange-900/20 border border-orange-800/30 rounded-lg p-5">
                  <h3 className="text-lg font-bold text-orange-400 mb-4 flex items-center">
                    <FileText className="h-5 w-5 mr-2" />
                    Workflow Integration
                  </h3>
                  
                  <div className="space-y-3">
                    <div className="bg-black/60 rounded p-3 border border-slate-800">
                      <div className="text-sm font-medium text-white mb-2">L1 Analyst Workflow</div>
                      <ul className="text-xs text-slate-300 space-y-1">
                        <li>1. Extract IOCs from AlertStrike alert</li>
                        <li>2. Submit to Intel AI for rapid analysis</li>
                        <li>3. Review consolidated reputation report</li>
                        <li>4. Make TP/FP determination</li>
                        <li>5. Escalate or close with documentation</li>
                      </ul>
                    </div>
                    
                    <div className="bg-black/60 rounded p-3 border border-slate-800">
                      <div className="text-sm font-medium text-white mb-2">L2/L3 Advanced Usage</div>
                      <ul className="text-xs text-slate-300 space-y-1">
                        <li>1. Deep IOC correlation and analysis</li>
                        <li>2. CVE research and exploit context</li>
                        <li>3. Malware family identification</li>
                        <li>4. AI-assisted rule creation</li>
                        <li>5. Threat hunting hypothesis development</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default AlertStrikeSIEM;
